/**
 * Gestion du menu principal
 * - Support burger menu mobile
 * - Gestion ouverture / fermeture de sous-menus
 * - Fermeture automatique des sous-menus quand un autre s’ouvre (accordéon)
 * - Fermeture du menu/sous-menus au clic extérieur
 * - Support hover sur desktop
 *
 * @author Dylan TARDIF AMMOUR
 * par artwaï pour artwaï
 */

const menu = {
    isMobile: false, // Détection mobile (selon visibilité du burger)
    isTouchDevice: window.matchMedia("(hover: none) and (pointer: coarse)").matches,

    // Selectors
    not_loaded_class: "js-not-loaded",
    burger_button_selector: ".header-menu-toggle",
    submenu_selector: ".sub-menu",
    menu_selector: false,
    main_selector: "#main",
    footer_selector: "#footer",

    // HTML elements
    footer: false,
    main: false,
    menu: false,
    burger_button: false,

    // Cache pour les triggers de sous-menus
    _submenuTriggers: false,

    /**
     * Initialise le menu
     * - Récupère le bouton burger et son aria-controls
     * - Définit le menu principal
     * - Applique l’état initial (fermé si mobile)
     */
    init() {
        this.burger_button = document.querySelector(this.burger_button_selector);

        if (!this.burger_button) {
            console.warn("Menu.js - Bouton menu introuvable");
            return;
        }

        this.isMobile = this.isButtonVisible();
        this.menu = this.burger_button.ariaControlsElements[0];

        if(!this.menu) {
            console.warn(`Menu.js - Le menu avec l'id ${this.burger_button.getAttribute("aria-controls")} est introuvable`);
            return;
        }

        // Retire la classe de non chargement
        this.menu.classList.remove(this.not_loaded_class);

        this.event();
    },

    /**
     * Initialise les écouteurs d’événements
     * - Clic global (toggle ou fermeture externe)
     * - Hover pour desktop
     */
    event() {
        document.addEventListener("click", (e) => {
            const trigger = e.target.closest("[aria-controls]");

            // ✅ Si clic sur un bouton de contrôle (burger ou sous-menu)
            if (trigger) {
                this.toggleAttributes(trigger);
                return;
            }

            // ✅ Si le clic est à l'intérieur du menu principal → ne rien faire
            if (this.menu.contains(e.target)) {
                return;
            }

            // ✅ Sinon → clic à l’extérieur → on ferme tous les sous-menus
            this.closeAllSubmenus();
        });

        // Pas de hover sur mobile
        if(!this.isTouchDevice) {

            // Ouverture forcée au survol
            this.menu.addEventListener("mouseenter", (e) => {
                const trigger = e.target.closest("[aria-controls]");

                if(!trigger || trigger.ariaControlsElements[0] === this.menu) return;

                this.toggleAttributes(trigger, true); // force ouverture
            }, true);

            // Fermeture au mouseleave
            this.menu.addEventListener("mouseleave", (e) => {
                const target = e.target,
                    related = e.relatedTarget
                ;

                let trigger = target.closest("[aria-controls]");
                let submenu;

                if (!trigger) {
                    // Cas : sortie depuis le sous-menu
                    submenu = target.closest(`${this.submenu_selector}`);
                    if (submenu) {
                        trigger = this.menu.querySelector(`[aria-controls="${submenu.id}"]`);
                    }
                }

                if (!trigger) return;

                submenu = submenu || trigger.ariaControlsElements[0];

                // Si la souris reste dans le trigger ou le sous-menu → pas de fermeture
                if (related && (trigger.contains(related) || (submenu && submenu.contains(related)))) return;

                this.toggleAttributes(trigger, false);
            }, true);
        }
    },

    /**
     * Récupère les triggers de sous-menus (en cache après premier appel)
     * @returns {HTMLElement[]} Liste des éléments déclencheurs de sous-menus
     */
    get submenuTriggers() {
        if (!this._submenuTriggers) {
            this._submenuTriggers = [...this.menu.querySelectorAll("[aria-controls]")]
                .filter(el => el.ariaControlsElements[0] !== this.menu);
        }
        return this._submenuTriggers;
    },

    /**
     * Ferme tous les sous-menus (sauf éventuellement un)
     * @param {string|null} exceptControl - ID du sous-menu à ne pas fermer
     */
    closeAllSubmenus(exceptControl = null) {
        for (const submenu of this.submenuTriggers) {
            const control = submenu.ariaControlsElements[0];

            if (control !== exceptControl && submenu.ariaExpanded === "true") {
                this.toggleAttributes(submenu, false);
            }
        }
    },

    /**
     * Ouvre ou ferme un menu ou sous-menu
     * @param {HTMLElement} element - Le trigger cliqué ou survolé
     * @param {boolean|null} force - true = forcer ouverture, false = forcer fermeture, null = toggle
     */
    toggleAttributes(element, force = null) {
        const expanded = element.ariaExpanded === "true",
            control = element.ariaControlsElements[0],
            newState = (force === null) ? !expanded : Boolean(force)
        ;

        if (newState === expanded) return;

        // Si on ouvre un sous-menu, fermer les autres
        if (control !== this.menu && newState === true) {
            this.closeAllSubmenus(control);
        }

        // Mettre à jour tous les triggers liés à ce contrôle
        for (const trigger of [...document.querySelectorAll(`[aria-controls="${control.id}"]`)]) {
            trigger.ariaExpanded = String(newState);
        }

        // Gestion spécifique du menu principal
        if(control === this.menu) {
            document.documentElement.classList.toggle("no-scroll", newState);
            document.body.classList.toggle("no-scroll", newState);

            if(!this.main) {
                this.main = document.querySelector(this.main_selector);
            }

            if(!this.footer) {
                this.footer = document.querySelector(this.footer_selector);
            }

            if (this.main) this.main.inert = newState;
            if (this.footer) this.footer.inert = newState;

            return;
        }
    },

    /**
     * Vérifie si le bouton est visible (utile pour déterminer si on est en mobile)
     * @returns {boolean}
     */
    isButtonVisible() {
        if (typeof this.burger_button.checkVisibility === "function") {
            return this.burger_button.checkVisibility();
        }

        // fallback pour Safari/anciens navigateurs
        const style = window.getComputedStyle(this.burger_button);
        return (
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            parseFloat(style.opacity) > 0 &&
            this.burger_button.getClientRects().length > 0
        );
    },
};

// Init au chargement DOM
document.addEventListener("DOMContentLoaded", function () {
    menu.init();
});
